import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePubliciteFromMandat } from '@app/hooks/usePubliciteFromMandat';
import {
    Button,
    Typography,
    Card,
    Divider,
    Stack,
    Box,
    Chip,
    Input,
    Textarea,
    Select,
    Option,
    Switch,
    Alert,
    IconButton,
    useTheme,
} from '@mui/joy';
import { AppIcon } from '@app/components/AppIcon';
import AppLoading from '@app/components/AppLoading';
import SearchAutocomplete from '@app/main/forms/components/SearchAutocomplete';
import {
    ParrainagesReconductions,
    getParrainagesReconductionsById,
    updateParrainagesReconductions,
} from '@app/api/data/parrainages-reconductions';
import {
    ParrainagesReconductionsModifie,
    getParrainagesReconductionsModifieByOriginalId,
    validateAndCreateParrainage,
} from '@app/api/data/parrainages-reconductions-modifie';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestoreIcon from '@mui/icons-material/Restore';
import axios from '@app/config/axios.config';
import { AppModal } from '@app/components/AppModal';
import { useAuth } from '@app/system/context/authContext';
import AppClubNameDisplay from '@app/components/AppClubNameDisplay';
import { titreValueMap } from '@app/Modules/titreValueMap';

interface FieldConfig {
    key: string;
    label: string;
    type:
        | 'text'
        | 'textarea'
        | 'select'
        | 'boolean'
        | 'date'
        | 'number'
        | 'autocomplete';
    options?: { value: string; label: string }[];
    section: string;
    apiUrl?: string;
    keyDisplay?: string;
    dbSendKey?: string;
    formatLabel?: (item: any) => string;
    required?: boolean;
}

const fieldConfigs: FieldConfig[] = [
    // Informations générales
    {
        key: 'status',
        label: 'Statut',
        type: 'select',
        section: 'Informations générales',
        options: [
            { value: 'A_traiter', label: 'À traiter' },
            { value: 'Ne_pas_appeler', label: 'Ne pas appeler' },
            { value: 'Refus', label: 'Refus/Reconduction non renouvelée' },
            { value: 'Rappeler', label: 'Rappeler' },
            { value: 'Sans_reponse', label: 'Sans réponse' },
            { value: 'Doc_envoyee', label: 'Document envoyé' },
            { value: 'Parrainage', label: 'Parrainage' },
        ],
    },
    {
        key: 'langue',
        label: 'Langue',
        type: 'select',
        section: 'Informations générales',
        options: [
            { value: 'FR', label: 'FR' },
            { value: 'DE', label: 'DE' },
        ],
    },
    {
        key: 'raisonSociale',
        label: 'Raison Sociale',
        type: 'text',
        section: 'Informations générales',
    },
    {
        key: 'titre',
        label: 'Titre',
        type: 'select',
        section: 'Informations générales',
        options: titreValueMap.map((item) => ({
            value: item.value,
            label: item.value,
        })),
    },
    {
        key: 'prenom1',
        label: 'Prénom 1',
        type: 'text',
        section: 'Informations générales',
    },
    {
        key: 'nom1',
        label: 'Nom 1',
        type: 'text',
        section: 'Informations générales',
    },
    {
        key: 'prenom2',
        label: 'Prénom 2',
        type: 'text',
        section: 'Informations générales',
    },
    {
        key: 'nom2',
        label: 'Nom 2',
        type: 'text',
        section: 'Informations générales',
    },

    // Adresse
    { key: 'adresse', label: 'Adresse', type: 'text', section: 'Adresse' },
    { key: 'cp', label: 'CP', type: 'text', section: 'Adresse' },
    {
        key: 'numeroPostal',
        label: 'N° Postal',
        type: 'text',
        section: 'Adresse',
    },
    { key: 'localite', label: 'Localité', type: 'text', section: 'Adresse' },

    // Contact
    {
        key: 'telephone1',
        label: 'Téléphone 1',
        type: 'text',
        section: 'Contact',
    },
    {
        key: 'telephone2',
        label: 'Téléphone 2',
        type: 'text',
        section: 'Contact',
    },
    { key: 'email', label: 'Email', type: 'text', section: 'Contact', required: true },

    // Informations commerciales
    {
        key: 'raisonSocialePj',
        label: 'Raison Sociale PJ',
        type: 'text',
        section: 'Informations commerciales',
    },
    {
        key: 'libelleFacture',
        label: 'Libellé Facture',
        type: 'textarea',
        section: 'Informations commerciales',
    },
    {
        key: 'correspondance',
        label: 'Correspondance',
        type: 'textarea',
        section: 'Informations commerciales',
    },
    {
        key: 'accordDe',
        label: 'Accord De',
        type: 'text',
        section: 'Informations commerciales',
    },
    {
        key: 'dateAccord',
        label: 'Date Accord',
        type: 'date',
        section: 'Informations commerciales',
        required: true,
    },

    // Informations internes

    {
        key: 'noClub',
        label: 'N° Club',
        type: 'autocomplete',
        section: 'Informations internes',
        apiUrl: '/mandats',
        formatLabel: (item: any) => `${item.ref} - (${item.club.nom})`,
        dbSendKey: 'ref',
        required: true,
    },
    {
        key: 'ca',
        label: 'CA',
        type: 'number',
        section: 'Informations internes',
        required: true,
    },

    {
        key: 'publicite',
        label: 'Publicité',
        type: 'select',
        section: 'Informations internes',
        required: true,
        options: [], // Will be populated dynamically
    },
    {
        key: 'infosInternes',
        label: 'Infos Internes',
        type: 'textarea',
        section: 'Informations internes',
    },
    {
        key: 'reconduction',
        label: 'Reconduction',
        type: 'boolean',
        section: 'Informations internes',
    },
    {
        key: 'annonceOk',
        label: 'Annonce OK',
        type: 'boolean',
        section: 'Informations internes',
    },
];

const ParrainagesReconductionsDetailsEnhanced = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isGranted } = useAuth();
    const theme = useTheme();
    const isAdminOrUser = isGranted(['ADMIN', 'USER']);
    const [parrainageReconduction, setParrainageReconduction] =
        useState<ParrainagesReconductions | null>(null);
    const [parrainageReconductionModifie, setParrainageReconductionModifie] =
        useState<ParrainagesReconductionsModifie | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [editMode, setEditMode] = useState(false);
    const [editedValues, setEditedValues] = useState<any>({});
    const [modifiedFields, setModifiedFields] = useState<Set<string>>(
        new Set()
    );
    const [validating, setValidating] = useState(false);
    const [originalId, setOriginalId] = useState<string | null>(null);
    const [showValidateModal, setShowValidateModal] = useState(false);
    const [validatedMsg, setValidatedMsg] = useState<string | null>(null);
    const [mandatIdForPublicite, setMandatIdForPublicite] = useState<
        number | null
    >(null);

    // Get current mandat ref (noClub) and convert to mandatId
    const currentMandatRef = useMemo(() => {
        const baseData = parrainageReconductionModifie || parrainageReconduction;
        return editedValues.noClub || baseData?.noClub;
    }, [parrainageReconduction, parrainageReconductionModifie, editedValues.noClub]);

    // Fetch publicite options from mandat
    const { publiciteOptions } = usePubliciteFromMandat(mandatIdForPublicite);

    // Fetch mandat ID when mandat ref changes
    useEffect(() => {
        const fetchMandatId = async () => {
            if (currentMandatRef) {
                try {
                    const response = await axios.get(`/mandats`, {
                        params: { search: currentMandatRef, limit: 1 },
                    });
                    if (response.data.result[0].id !== null) {
                        setMandatIdForPublicite(response.data.result[0].id);
                    }
                } catch (error) {
                    console.error('Error fetching mandat ID:', error);
                }
            }
        };
        fetchMandatId();
    }, [currentMandatRef]);

    // Update publicite field options dynamically
    const fieldConfigsWithPublicites = useMemo(() => {
        return fieldConfigs.map((field) => {
            if (field.key === 'publicite' && publiciteOptions.length > 0) {
                return {
                    ...field,
                    options: publiciteOptions.map((opt) => ({
                        value: opt,
                        label: opt,
                    })),
                };
            }
            return field;
        });
    }, [publiciteOptions]);

    const normalizeDateForComparison = (dateValue: unknown): string => {
        if (!dateValue) return '';
        const date = new Date(String(dateValue));
        if (!isNaN(date.getTime())) {
            return date.toISOString().split('T')[0];
        }
        return String(dateValue).split('T')[0];
    };

    // Fonction pour vérifier si tous les champs requis sont remplis
    const checkRequiredFields = () => {
        const baseData = parrainageReconductionModifie || parrainageReconduction;
        // Fusionner avec les valeurs en cours d'édition
        const currentData = editMode
            ? { ...baseData, ...editedValues }
            : baseData;
        const requiredFields = fieldConfigsWithPublicites
            .filter((field) => field.required)
            .map((field) => field.key);

        if (!currentData) {
            return {
                isValid: false,
                missingFields: requiredFields,
                isDateAccordModified: false,
            };
        }

        const missingFields = requiredFields.filter((fieldKey) => {
            const value = (currentData as any)[fieldKey];
            // Vérifier si le champ est vide, null, undefined ou 0
            return !value || value === '' || value === 0 || value === '0';
        });

        const originalDateAccord = normalizeDateForComparison(
            parrainageReconduction?.dateAccord
        );
        const currentDateAccord = normalizeDateForComparison(
            (currentData as any)?.dateAccord
        );
        const isDateAccordModified =
            currentDateAccord !== '' && currentDateAccord !== originalDateAccord;

        return {
            isValid: missingFields.length === 0,
            missingFields,
            isDateAccordModified,
        };
    };

    const {
        isValid: hasRequiredFields,
        missingFields,
        isDateAccordModified,
    } = checkRequiredFields();
    const canChangeStatus = hasRequiredFields && isDateAccordModified;

    // Fonction pour obtenir le label d'un champ
    const getFieldLabel = (fieldKey: string) => {
        const field = fieldConfigsWithPublicites.find(
            (f) => f.key === fieldKey
        );
        return field ? field.label : fieldKey;
    };

    // Fonction pour formater les dates
    const formatDate = (date: string | null | undefined) => {
        if (!date) return '—';
        try {
            return new Date(date).toLocaleDateString('fr-FR');
        } catch {
            return date;
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const fetchDetails = async () => {
        if (!id) return;

        setLoading(true);
        try {
            // D'abord, essayer de récupérer comme un ParrainagesReconductions original
            let potentielOriginalId = id;

            try {
                const responsePotentiel = await getParrainagesReconductionsById(
                    id
                );
                setParrainageReconduction(responsePotentiel.data);
                setOriginalId(id);
            } catch (error) {
                console.log('error->', error);
                // Si ça échoue, c'est peut-être un ID de ParrainagesReconductionsModifie
                // Dans ce cas, récupérer d'abord le modifié pour obtenir l'ID original
                try {
                    const responseModifie = await axios.get(
                        `/parrainages-reconductions-modifie/${id}`
                    );
                    if (
                        responseModifie.data &&
                        responseModifie.data.parrainagesReconductionsId
                    ) {
                        potentielOriginalId =
                            responseModifie.data.parrainagesReconductionsId.toString();
                        setOriginalId(potentielOriginalId);
                        setParrainageReconductionModifie(responseModifie.data);

                        // Maintenant récupérer l'original avec le bon ID
                        const responsePotentiel =
                            await getParrainagesReconductionsById(
                                potentielOriginalId
                            );
                        setParrainageReconduction(responsePotentiel.data);
                    } else {
                        throw new Error('Potentiel parrainage non trouvé');
                    }
                } catch (innerError) {
                    console.error(
                        'Erreur lors de la récupération:',
                        innerError
                    );
                    throw innerError;
                }
            }

            // Maintenant chercher les modifications avec l'ID original
            try {
                const responseModifie =
                    await getParrainagesReconductionsModifieByOriginalId(
                        potentielOriginalId
                    );
                // Since the endpoint returns an array, take the first (most recent) one
                if (responseModifie.data) {
                    setParrainageReconductionModifie(responseModifie.data);

                    // Identifier les champs modifiés
                    const modified = new Set<string>();
                    const potentielData =
                        parrainageReconduction ||
                        (
                            await getParrainagesReconductionsById(
                                potentielOriginalId
                            )
                        ).data;
                    fieldConfigsWithPublicites.forEach((field) => {
                        const originalValue = potentielData[field.key];
                        const modifiedValue =
                            responseModifie.data[0][field.key];

                        // Comparer les valeurs en tenant compte des types
                        if (field.type === 'boolean') {
                            if (
                                Boolean(originalValue) !==
                                Boolean(modifiedValue)
                            ) {
                                modified.add(field.key);
                            }
                        } else if (field.type === 'number') {
                            if (
                                Number(originalValue || 0) !==
                                Number(modifiedValue || 0)
                            ) {
                                modified.add(field.key);
                            }
                        } else {
                            // Pour les autres types, comparer les valeurs converties en string
                            if (
                                String(originalValue || '') !==
                                String(modifiedValue || '')
                            ) {
                                modified.add(field.key);
                            }
                        }
                    });
                    setModifiedFields(modified);
                }
            } catch (error) {
                console.log('Aucun potentiel parrainage modifié trouvé.');
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des détails:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        setEditMode(true);
        setEditedValues({});
    };

    const handleCancel = () => {
        setEditMode(false);
        setEditedValues({});
    };

    const handleFieldChange = (key: string, value: any) => {
        console.log('Field change:', key, value);
        setEditedValues((prev: any) => ({ ...prev, [key]: value }));
    };

    const handleResetField = (key: string) => {
        setEditedValues((prev: any) => {
            const newValues = { ...prev };
            delete newValues[key];
            return newValues;
        });
    };

    const handleSave = async () => {
        if (!originalId) return;

        // Vérifier si le statut "Parrainage" est sélectionné et si les champs requis manquent
        const baseData = parrainageReconductionModifie || parrainageReconduction;
        const mergedData = { ...baseData, ...editedValues };
        const selectedStatus = mergedData.status;

        if (selectedStatus === 'Parrainage' && !canChangeStatus) {
            const missingFieldsText =
                missingFields.length > 0
                    ? `Les champs suivants doivent être remplis : ${missingFields
                          .map((field) => getFieldLabel(field))
                          .join(', ')}`
                    : '';
            const dateAccordText = !isDateAccordModified
                ? "La Date Accord doit être modifiée (différente de la date d'accord d'origine)."
                : '';
            alert(
                [`Impossible de sauvegarder avec le statut "Parrainage".`, missingFieldsText, dateAccordText]
                    .filter(Boolean)
                    .join('\n')
            );
            return;
        }

        try {
            // Vérifier si le statut a changé pour faire l'appel API spécifique
            const currentStatus = parrainageReconduction?.status;
            const newStatus = mergedData.status;
            const statusChanged = currentStatus !== newStatus;

            // Sauvegarder les autres champs (exclure le statut qui sera géré séparément)
            const dataToSave = { ...mergedData };
            const fieldsToRemove = [
                'parrainageId',
                'id',
                'createdAt',
                'updatedAt',
                'deletedAt',
                'parrainagesReconductionsModifieId',
                'parrainagesReconductionsId',
                'validated',
                'collaborateurId',
                'collaborateur',
                'mandatId',
                'mandat',
                'status', // Exclure le statut pour le traiter séparément
            ];
            fieldsToRemove.forEach((field) => {
                delete dataToSave[field];
            });

            // Sauvegarder les autres champs d'abord
            await updateParrainagesReconductions(
                parrainageReconduction?.id.toString() ?? '',
                dataToSave
            );

            // Si le statut a changé, faire l'appel API spécifique
            if (statusChanged && newStatus) {
                await axios.patch(
                    `/parrainages-reconductions/${originalId}/status`,
                    {
                        status: newStatus,
                    }
                );
            }

            fetchDetails();
            setEditMode(false);
            setEditedValues({});
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            alert('Erreur lors de la sauvegarde');
        }
    };

    const handleValidate = async () => {
        if (!parrainageReconductionModifie) {
            alert('Aucune modification à valider');
            return;
        }

        setShowValidateModal(true);
    };

    const handleConfirmValidation = async () => {
        if (!parrainageReconductionModifie) {
            alert('Veuillez sélectionner un mandat et un collaborateur');
            return;
        }

        setValidating(true);
        try {
            await validateAndCreateParrainage(
                parrainageReconduction?.id.toString() ?? ''
            );
            setValidatedMsg('Parrainage créé avec succès !');

            // Attendre un peu pour afficher le message, puis rafraîchir les données
            setTimeout(async () => {
                await fetchDetails();
                setShowValidateModal(false);
                setValidatedMsg(null);
            }, 2000);
        } catch (error) {
            console.error('Erreur lors de la validation:', error);
            alert('Erreur lors de la validation du parrainage');
        } finally {
            setValidating(false);
        }
    };

    const renderField = (field: FieldConfig) => {
        const originalValue = (parrainageReconduction as any)?.[field.key];
        const modifiedValue = (parrainageReconductionModifie as any)?.[field.key];

        // En mode édition, utiliser la valeur éditée si elle existe, sinon la valeur actuelle
        const currentValue = editMode
            ? field.key in editedValues
                ? editedValues[field.key]
                : modifiedValue ?? originalValue
            : modifiedValue ?? originalValue;

        const isModified = modifiedFields.has(field.key);

        if (editMode) {
            switch (field.type) {
                case 'textarea':
                    return (
                        <Textarea
                            value={currentValue || ''}
                            onChange={(e) =>
                                handleFieldChange(field.key, e.target.value)
                            }
                            minRows={2}
                            size="sm"
                            sx={{ width: '100%' }}
                        />
                    );
                case 'select':
                    // For select fields, ensure current value is in options
                    const options = field.options || [];
                    const valueExists = options.some(
                        (opt) =>
                            opt.value.trim().toLowerCase() ===
                            String(currentValue || '')
                                .trim()
                                .toLowerCase()
                    );

                    // If current value doesn't exist in options and is not empty, add it
                    const optionsWithCurrentValue =
                        currentValue && !valueExists
                            ? [
                                  {
                                      value: String(currentValue),
                                      label: `${currentValue} (ancienne valeur)`,
                                  },
                                  ...options,
                              ]
                            : options;

                    return (
                        <Select
                            value={currentValue || ''}
                            onChange={(_, value) =>
                                handleFieldChange(field.key, value)
                            }
                            size="sm"
                            sx={{ width: '100%' }}
                        >
                            {optionsWithCurrentValue.map((opt) => (
                                <Option
                                    key={opt.value}
                                    value={opt.value}
                                    sx={
                                        currentValue &&
                                        opt.value.trim().toLowerCase() ===
                                            String(currentValue)
                                                .trim()
                                                .toLowerCase() &&
                                        !valueExists
                                            ? {
                                                  fontStyle: 'italic',
                                                  opacity: 0.7,
                                              }
                                            : {}
                                    }
                                >
                                    {opt.label}
                                </Option>
                            ))}
                        </Select>
                    );
                case 'boolean':
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Switch
                                checked={currentValue || false}
                                onChange={(e) =>
                                    handleFieldChange(
                                        field.key,
                                        e.target.checked
                                    )
                                }
                                size="sm"
                                color="primary"
                                variant="soft"
                            />
                            <Typography level="body-sm" sx={{ ml: 1 }}>
                                {currentValue ? 'Oui' : 'Non'}
                            </Typography>
                        </Box>
                    );
                case 'date':
                    return (
                        <Input
                            type="date"
                            value={currentValue || ''}
                            onChange={(e) =>
                                handleFieldChange(field.key, e.target.value)
                            }
                            size="sm"
                            sx={{ width: '100%' }}
                        />
                    );
                case 'number':
                    return (
                        <Input
                            type="number"
                            value={currentValue || ''}
                            onChange={(e) =>
                                handleFieldChange(
                                    field.key,
                                    parseFloat(e.target.value) || 0
                                )
                            }
                            size="sm"
                            sx={{ width: '100%' }}
                        />
                    );
                case 'autocomplete':
                    return (
                        <SearchAutocomplete
                            apiUrl={field.apiUrl || ''}
                            keyDisplay={field.keyDisplay || ''}
                            formatLabel={field.formatLabel || undefined}
                            dbSendKey={field.dbSendKey}
                            onChange={(value) =>
                                handleFieldChange(field.key, value)
                            }
                            initialId={currentValue ? currentValue : null}
                        />
                    );
                default:
                    return (
                        <Input
                            value={currentValue || ''}
                            onChange={(e) =>
                                handleFieldChange(field.key, e.target.value)
                            }
                            size="sm"
                            sx={{ width: '100%' }}
                        />
                    );
            }
        }

        // Gestion spéciale pour le champ noClub - afficher ref + nom du club
        if (field.key === 'noClub' && currentValue) {
            return (
                <Typography
                    level="body-sm"
                    color={isModified ? 'primary' : 'neutral'}
                >
                    <AppClubNameDisplay mandatRef={currentValue} />
                </Typography>
            );
        }

        return (
            <Typography
                level="body-sm"
                color={isModified ? 'primary' : 'neutral'}
            >
                {field.type === 'boolean'
                    ? currentValue
                        ? 'Oui'
                        : 'Non'
                    : field.type === 'date'
                    ? formatDate(currentValue)
                    : currentValue || '—'}
            </Typography>
        );
    };

    const renderFieldOriginalValue = (
        field: FieldConfig,
        originalValue: any
    ) => {
        if (field.key === 'status') {
            return <Typography level="body-sm">À traiter</Typography>;
        }
        if (field.type === 'boolean') {
            return (
                <Typography level="body-sm">
                    {originalValue ? 'Oui' : 'Non'}
                </Typography>
            );
        }
        if (field.type === 'date') {
            return (
                <Typography level="body-sm">
                    {formatDate(originalValue)}
                </Typography>
            );
        }
        if (field.key === 'noClub') {
            return (
                <Typography level="body-sm">
                    {originalValue ? (
                        <AppClubNameDisplay mandatRef={originalValue} />
                    ) : (
                        '—'
                    )}
                </Typography>
            );
        }
        return <Typography level="body-sm">{originalValue || '—'}</Typography>;
    };

    const renderComparisonView = () => {
        const sections = [
            ...new Set(fieldConfigsWithPublicites.map((f) => f.section)),
        ];

        return (
            <Box>
                {sections.map((section) => (
                    <Box key={section} sx={{ mb: 3 }}>
                        {/* Afficher les boutons d'édition à droit du titre pour 'Informations générales' */}
                        {section === 'Informations générales' ? (
                            <div className="my-4">
                                <Typography
                                    level="title-md"
                                    color="primary"
                                    sx={{ ml: 2 }}
                                >
                                    {section}
                                </Typography>
                            </div>
                        ) : (
                            <Typography
                                level="title-md"
                                color="primary"
                                sx={{ mb: 2 }}
                            >
                                {section}
                            </Typography>
                        )}
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ overflowX: 'auto' }}>
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th
                                            style={{
                                                textAlign: 'left',
                                                padding: '8px',
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                width: '25%',
                                            }}
                                        >
                                            Champ
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'left',
                                                padding: '8px',
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                width: '30%',
                                            }}
                                        >
                                            Valeur originale
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'left',
                                                padding: '8px',
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                width: '35%',
                                            }}
                                        >
                                            Valeur modifiée
                                        </th>
                                        <th
                                            style={{
                                                textAlign: 'center',
                                                padding: '8px',
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                width: '10%',
                                            }}
                                        >
                                            Statut
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fieldConfigsWithPublicites
                                        .filter((f) => f.section === section)
                                        .map((field) => {
                                            const originalValue = (
                                                parrainageReconduction as any
                                            )?.[field.key];
                                            const modifiedValue = (
                                                parrainageReconductionModifie as any
                                            )?.[field.key];
                                            const isModified =
                                                originalValue !==
                                                (modifiedValue ??
                                                    originalValue);
                                            const isCurrentlyEdited =
                                                editMode &&
                                                field.key in editedValues;

                                            return (
                                                <tr
                                                    key={field.key}
                                                    style={{
                                                        backgroundColor:
                                                            isModified
                                                                ? theme.palette
                                                                      .primary
                                                                      .softBg
                                                                : 'transparent',
                                                        borderLeft:
                                                            isCurrentlyEdited
                                                                ? `3px solid ${theme.palette.primary.solidBg}`
                                                                : 'none',
                                                    }}
                                                >
                                                    <td
                                                        style={{
                                                            padding: '12px 8px',
                                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Typography
                                                                level="body-sm"
                                                                fontWeight="md"
                                                            >
                                                                {field.label}
                                                            </Typography>
                                                            {field.required && (
                                                                <Chip
                                                                    size="sm"
                                                                    color="danger"
                                                                    variant="soft"
                                                                >
                                                                    Requis
                                                                </Chip>
                                                            )}
                                                        </Box>
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '12px 8px',
                                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                                        }}
                                                    >
                                                        <Typography level="body-sm">
                                                            {renderFieldOriginalValue(
                                                                field,
                                                                originalValue
                                                            )}
                                                        </Typography>
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: editMode
                                                                ? '12px 32px 12px 8px'
                                                                : '12px 8px',
                                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                                            position:
                                                                'relative',
                                                        }}
                                                    >
                                                        {renderField(field)}
                                                        {editMode &&
                                                            isCurrentlyEdited && (
                                                                <IconButton
                                                                    size="sm"
                                                                    color="neutral"
                                                                    variant="plain"
                                                                    onClick={() =>
                                                                        handleResetField(
                                                                            field.key
                                                                        )
                                                                    }
                                                                    title="Réinitialiser cette valeur"
                                                                    sx={{
                                                                        position:
                                                                            'absolute',
                                                                        right: -5,
                                                                        top: '50%',
                                                                        transform:
                                                                            'translateY(-50%)',
                                                                        opacity: 0.7,
                                                                        '&:hover':
                                                                            {
                                                                                opacity: 1,
                                                                            },
                                                                    }}
                                                                >
                                                                    <RestoreIcon
                                                                        sx={{
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                            )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: '12px 8px',
                                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        {isCurrentlyEdited ? (
                                                            <Chip
                                                                size="sm"
                                                                color="success"
                                                                variant="soft"
                                                            >
                                                                En cours
                                                            </Chip>
                                                        ) : isModified ? (
                                                            <Chip
                                                                size="sm"
                                                                color="primary"
                                                                variant="soft"
                                                            >
                                                                Modifié
                                                            </Chip>
                                                        ) : (
                                                            <Chip
                                                                size="sm"
                                                                color="neutral"
                                                                variant="soft"
                                                            >
                                                                Inchangé
                                                            </Chip>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                }}
            >
                <AppLoading />
            </Box>
        );
    }

    if (!parrainageReconduction) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography level="h4" color="danger">
                    Potentiel parrainage non trouvé
                </Typography>
                <Button
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/parrainagesReconductions')}
                    startDecorator={<AppIcon icon="ArrowLeftIcon" />}
                >
                    Retour à la liste
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Stack
                direction="row"
                spacing={2}
                sx={{ mb: 3 }}
                alignItems="center"
            >
                <Button
                    variant="outlined"
                    color="neutral"
                    startDecorator={<AppIcon icon="ArrowLeftIcon" />}
                    onClick={() => navigate(-1)}
                >
                    Retour
                </Button>

                <Box sx={{ flex: 1 }} />

                {modifiedFields.size > 0 &&
                    parrainageReconductionModifie &&
                    !parrainageReconduction?.validated && (
                        <Alert color="primary" variant="soft" sx={{ py: 0.5 }}>
                            {modifiedFields.size} modification
                            {modifiedFields.size > 1 ? 's' : ''} en attente
                        </Alert>
                    )}
                {parrainageReconduction?.validated && (
                    <Alert color="success" variant="soft" sx={{ py: 0.5 }}>
                        <Typography level="body-sm" color="success">
                            Ce parrainage potentiel a été validé.
                        </Typography>
                    </Alert>
                )}

                {isAdminOrUser &&
                    !parrainageReconduction?.validated &&
                    !editMode && (
                        <>
                            <Button
                                variant="soft"
                                color="success"
                                startDecorator={<CheckCircleIcon />}
                                onClick={handleValidate}
                                loading={validating}
                            >
                                Valider et créer le parrainage
                            </Button>
                        </>
                    )}
            </Stack>

            <Card sx={{ p: 3 }} className="w-full">
                <div className="justifiy-between flex w-full">
                    <div className="">
                        <Typography className="whitespace-nowrap" level="h4">
                            Parrainage potentiel
                        </Typography>
                    </div>

                    <div className="flex w-full flex-col justify-between gap-2">
                        <div className="flex justify-end gap-2">
                            {!parrainageReconduction?.validated &&
                                (!editMode ? (
                                    <Button
                                        variant="soft"
                                        color="primary"
                                        startDecorator={<EditIcon />}
                                        onClick={handleEdit}
                                    >
                                        Modifier
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            variant="outlined"
                                            color="neutral"
                                            startDecorator={<CancelIcon />}
                                            onClick={handleCancel}
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            variant="soft"
                                            color="success"
                                            startDecorator={<SaveIcon />}
                                            onClick={handleSave}
                                            disabled={(() => {
                                                const baseData =
                                                    parrainageReconductionModifie ||
                                                    parrainageReconduction;
                                                const mergedData = {
                                                    ...baseData,
                                                    ...editedValues,
                                                };
                                                return (
                                                    mergedData.status ===
                                                        'Parrainage' &&
                                                    !canChangeStatus
                                                );
                                            })()}
                                        >
                                            Enregistrer
                                        </Button>
                                    </>
                                ))}
                        </div>
                        {editMode &&
                            (() => {
                                const baseData =
                                    parrainageReconductionModifie ||
                                    parrainageReconduction;
                                const mergedData = {
                                    ...baseData,
                                    ...editedValues,
                                };
                                const isSaveBlocked =
                                    mergedData.status === 'Parrainage' &&
                                    !canChangeStatus;

                                if (isSaveBlocked) {
                                    return (
                                        <Box className="flex w-full justify-end">
                                            <div>
                                                <Typography
                                                    level="body-sm"
                                                    color="warning"
                                                >
                                                    {missingFields.length > 0
                                                        ? missingFields.length > 1
                                                            ? 'Les champs suivants doivent être remplis :'
                                                            : 'Le champ suivant doit être rempli :'
                                                        : 'Le statut "Parrainage" est bloqué tant que la Date Accord n\'est pas modifiée.'}
                                                </Typography>
                                                {(missingFields.length > 0 || !isDateAccordModified) && (
                                                    <Box sx={{ mt: 1 }}>
                                                        {missingFields.map(
                                                            (field) => (
                                                                <Chip
                                                                    key={field}
                                                                    size="sm"
                                                                    color="danger"
                                                                    variant="soft"
                                                                    sx={{
                                                                        mr: 1,
                                                                        mb: 0.5,
                                                                    }}
                                                                >
                                                                    {getFieldLabel(
                                                                        field
                                                                    )}
                                                                </Chip>
                                                            )
                                                        )}
                                                        {!isDateAccordModified && (
                                                            <Chip
                                                                size="sm"
                                                                color="danger"
                                                                variant="soft"
                                                                sx={{ mr: 1, mb: 0.5 }}
                                                            >
                                                                Date Accord (doit être modifiée)
                                                            </Chip>
                                                        )}
                                                    </Box>
                                                )}
                                            </div>
                                        </Box>
                                    );
                                }
                                return null;
                            })()}
                    </div>
                </div>

                {renderComparisonView()}
            </Card>

            {/* Modal de validation */}
            <AppModal
                title="Valider et créer le parrainage"
                open={showValidateModal}
                onClose={() => setShowValidateModal(false)}
                submitText="Créer le parrainage"
                submitIcon="CheckCircleIcon"
                handleSubmit={handleConfirmValidation}
                disabelSubmit={validating}
                closeOnSubmit={true}
            >
                <Box className="flex items-center justify-center">
                    {validatedMsg ? (
                        <Alert color="success" variant="soft" sx={{ py: 0.5 }}>
                            {validatedMsg}
                        </Alert>
                    ) : (
                        <Alert color="primary" variant="soft" sx={{ py: 0.5 }}>
                            <Typography level="body-sm" color="primary">
                                Vous êtes sur le point de créer un parrainage à
                                partir de ce potentiel parrainage.
                            </Typography>
                        </Alert>
                    )}
                </Box>
            </AppModal>
        </Box>
    );
};

export default ParrainagesReconductionsDetailsEnhanced;
